import React, { useState, useRef } from 'react';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid,
    Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import './Products.css';
import './AllSellStyles.css';
import './CommonStyles.css';

// Mock data matching the screenshot
const mockData = [
    { name: 'Sandwitch – 43646 0\n(Single)', units: 131 },
    { name: 'BENTONVILLE BREWING GRAPEFRUIT\nWRECKER 6PK – 666628957708\n(Single)', units: 56 },
    { name: 'Body Amor Water –\n858176002140\n(Single)', units: 46 },
    { name: 'HIGHNOON WATERMELON VODKA &\nSODA 4 PK – 085000429259 (Single)', units: 39 },
    { name: '$10K STACKS –\n814542018016\n(Single)', units: 12 },
];

// Custom X-axis tick with line-wrapped text
const CustomXAxisTick = ({ x, y, payload }) => {
    const lines = payload.value.split('\n');
    return (
        <g transform={`translate(${x},${y})`}>
            {lines.map((line, i) => (
                <text
                    key={i}
                    x={0}
                    y={0}
                    dy={14 + i * 13}
                    textAnchor="middle"
                    fill="#64748b"
                    fontSize={11}
                >
                    {line}
                </text>
            ))}
        </g>
    );
};

const TrendingProductsReport = () => {
    const [showChartMenu, setShowChartMenu] = useState(false);
    const chartRef = useRef(null);

    const handlePrintChart = () => {
        window.print();
        setShowChartMenu(false);
    };

    return (
        <div style={{ paddingTop: '0.5rem' }}>

            {/* ── Top Trending Products Chart Card ── */}
            <div style={{
                background: '#fff',
                border: '1px solid #e2e8f0',
                borderRadius: '10px',
                padding: '1.5rem',
                boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                position: 'relative',
            }}>

                {/* Card Header */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span style={{ fontWeight: '700', fontSize: '1rem', color: '#1e293b' }}>
                            Top Trending Products
                        </span>
                        <i className="fa fa-info-circle" style={{ color: '#3b82f6', fontSize: '0.9rem', cursor: 'pointer' }} title="Based on total units sold" />
                    </div>

                    {/* Chart burger menu */}
                    <div style={{ position: 'relative' }}>
                        <button
                            onClick={() => setShowChartMenu(!showChartMenu)}
                            style={{
                                background: 'none',
                                border: '1px solid #e2e8f0',
                                borderRadius: '4px',
                                padding: '0.3rem 0.5rem',
                                cursor: 'pointer',
                                color: '#64748b',
                                lineHeight: 1,
                                fontSize: '1rem',
                            }}
                            title="Chart options"
                        >
                            ☰
                        </button>

                        {showChartMenu && (
                            <>
                                {/* Backdrop */}
                                <div
                                    style={{ position: 'fixed', inset: 0, zIndex: 99 }}
                                    onClick={() => setShowChartMenu(false)}
                                />
                                <div style={{
                                    position: 'absolute', top: '110%', right: 0,
                                    background: '#fff',
                                    border: '1px solid #e2e8f0',
                                    borderRadius: '6px',
                                    boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                                    minWidth: '190px',
                                    zIndex: 100,
                                    padding: '0.25rem 0',
                                }}>
                                    {[
                                        { icon: 'fa-arrows-alt', label: 'View in full screen' },
                                        { icon: 'fa-print', label: 'Print chart', action: handlePrintChart },
                                        null, // divider
                                        { icon: 'fa-file-image-o', label: 'Download PNG image' },
                                        { icon: 'fa-file-image-o', label: 'Download JPEG image' },
                                        { icon: 'fa-file-pdf-o', label: 'Download PDF document' },
                                        { icon: 'fa-code', label: 'Download SVG vector image' },
                                    ].map((item, i) =>
                                        item === null ? (
                                            <hr key={i} style={{ margin: '0.25rem 0', border: 'none', borderTop: '1px solid #f1f5f9' }} />
                                        ) : (
                                            <button
                                                key={i}
                                                onClick={item.action || (() => setShowChartMenu(false))}
                                                style={{
                                                    display: 'flex', alignItems: 'center', gap: '0.6rem',
                                                    width: '100%', background: 'none', border: 'none',
                                                    padding: '0.5rem 1rem', fontSize: '0.8125rem',
                                                    color: '#334155', cursor: 'pointer', textAlign: 'left',
                                                    transition: 'background 0.15s',
                                                }}
                                                onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                                                onMouseLeave={e => e.currentTarget.style.background = 'none'}
                                            >
                                                <i className={`fa ${item.icon}`} style={{ width: '14px', color: '#64748b' }} />
                                                {item.label}
                                            </button>
                                        )
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                </div>

                {/* Y-axis label + chart */}
                <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                    {/* Rotated Y-axis label */}
                    <div style={{
                        writingMode: 'vertical-rl',
                        transform: 'rotate(180deg)',
                        fontSize: '0.8rem',
                        color: '#64748b',
                        marginRight: '0.5rem',
                        alignSelf: 'center',
                        userSelect: 'none',
                    }}>
                        Values
                    </div>

                    {/* Chart */}
                    <div ref={chartRef} style={{ flex: 1, minHeight: 320 }}>
                        <ResponsiveContainer width="100%" height={320}>
                            <BarChart
                                data={mockData}
                                margin={{ top: 10, right: 20, left: 0, bottom: 80 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                <XAxis
                                    dataKey="name"
                                    tick={<CustomXAxisTick />}
                                    interval={0}
                                    height={90}
                                    axisLine={{ stroke: '#e2e8f0' }}
                                    tickLine={false}
                                />
                                <YAxis
                                    tick={{ fontSize: 11, fill: '#64748b' }}
                                    axisLine={false}
                                    tickLine={false}
                                    domain={[0, 150]}
                                    ticks={[0, 25, 50, 75, 100, 125, 150]}
                                />
                                <Tooltip
                                    contentStyle={{
                                        borderRadius: '8px',
                                        border: '1px solid #e2e8f0',
                                        fontSize: '0.8125rem',
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                    }}
                                    formatter={val => [`${val} units`, 'Total unit sold']}
                                    labelFormatter={label => label.replace(/\n/g, ' ')}
                                />
                                <Legend
                                    wrapperStyle={{ paddingTop: '1rem', fontSize: '0.8125rem', color: '#64748b' }}
                                    formatter={() => 'Total unit sold'}
                                />
                                <Bar
                                    dataKey="units"
                                    name="Total unit sold"
                                    fill="#60a5fa"
                                    radius={[3, 3, 0, 0]}
                                    maxBarSize={80}
                                />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TrendingProductsReport;
